import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { ArrowRight, BookOpen, Video, Award } from 'lucide-react';

const TonyProPaintingPage = () => {
    return (
        <div data-testid="tony-pro-painting-page">
            <SEO
                title="Tony Pro Alla Prima Painting Course — Portrait & Figure"
                description="Learn alla prima oil painting from award-winning artist Tony Pro. Multi-session portrait and figure painting course with tiered access. Lifetime video instruction at Zarolla."
                url="/tony-pro-painting-course"
                keywords={['Tony Pro', 'Tony Pro painting', 'alla prima course', 'portrait painting course', 'Tony Pro oil painting']}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'Course',
                    '@id': 'https://zarolla.com/tony-pro-painting-course#course',
                    url: 'https://zarolla.com/tony-pro-painting-course',
                    name: 'Tony Pro Alla Prima Painting Course',
                    description: 'Learn alla prima oil painting with Tony Pro. A structured, multi-session portrait and figure painting course covering the Zorn palette, value structure, and direct painting technique.',
                    provider: {
                        '@type': 'Organization',
                        name: 'Zarolla Online Art Academy',
                        url: 'https://zarolla.com',
                    },
                    instructor: {
                        '@type': 'Person',
                        name: 'Tony Pro',
                        jobTitle: 'Fine Artist and Art Instructor',
                    },
                    hasCourseInstance: {
                        '@type': 'CourseInstance',
                        courseMode: 'online',
                        courseWorkload: 'PT20H',
                    },
                    isPartOf: { '@id': 'https://zarolla.com/#website' },
                    datePublished: '2007-01-01',
                    dateModified: new Date().toISOString().split('T')[0],
                    inLanguage: 'en-US',
                }}
            />

            <div className="section-gap">
                <div className="container-main">
                    {/* Hero */}
                    <div className="max-w-3xl mb-16">
                        <span className="text-xs font-semibold uppercase tracking-widest text-accent">
                            Award-winning instruction
                        </span>
                        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-primary leading-tight mt-4">
                            Tony Pro Alla Prima Oil Painting
                        </h1>
                        <p className="text-muted-foreground text-lg mt-6 max-w-2xl">
                            Study portrait and figure painting with Tony Pro &mdash; Best in Show at the Oil Painters of America, First Place at the Portrait Society of America, and a student of the late Glen Orbik. A complete, multi-session course in direct oil painting technique.
                        </p>
                        <div className="flex flex-wrap gap-4 mt-8">
                            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base px-8 py-6" data-testid="tonypro-view-course-btn">
                                <Link to="/courses/7">
                                    View Course Details <ArrowRight className="ml-2 h-4 w-4" />
                                </Link>
                            </Button>
                            <Button asChild variant="outline" size="lg" className="text-base px-8 py-6" data-testid="tonypro-about-btn">
                                <Link to="/instructor/tony-pro">
                                    About Tony Pro
                                </Link>
                            </Button>
                        </div>
                    </div>

                    {/* Section 1 */}
                    <section className="py-12 border-t border-border" data-testid="tonypro-section-1">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                            <div>
                                <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                    A course built on real experience
                                </h2>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    Tony Pro is not a YouTube painter offering tips. He is a professional fine artist with decades of exhibition history, national awards, and serious training under <Link to="/instructor/glen-orbik" className="text-accent hover:underline">Glen Orbik</Link> and Richard Schmid. His instruction reflects that depth &mdash; it is practical, structured, and grounded in the principles that produce lasting skill.
                                </p>
                                <p className="text-muted-foreground leading-relaxed mb-4">
                                    His Zarolla course covers alla prima oil painting from the ground up: palette setup, value structure, color mixing for flesh, the block-in, building form, and finishing. Each session builds on the last, giving you a clear path through what can otherwise feel like an overwhelming subject.
                                </p>
                                <p className="text-muted-foreground leading-relaxed">
                                    Whether you're approaching oil painting for the first time or looking to strengthen your alla prima technique, Tony's instruction gives you a repeatable process &mdash; not just inspiration.
                                </p>
                            </div>
                            <div className="bg-secondary/30 rounded-sm p-8">
                                <h3 className="font-medium text-primary mb-4">What this course covers:</h3>
                                <ul className="space-y-3 text-muted-foreground">
                                    <li className="flex items-start gap-3">
                                        <BookOpen className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                                        <span>Alla prima portrait painting from start to finish</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Video className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                                        <span>Color mixing for flesh tones using a limited and full palette</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <Award className="h-4 w-4 text-accent mt-1 flex-shrink-0" />
                                        <span>Value structure, edge control, and paint handling</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <span className="text-accent mt-1 flex-shrink-0">&mdash;</span>
                                        <span>Tiered pricing &mdash; start with 3 classes or get the full 10-lesson course</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Section 2: Approach */}
                    <section className="py-12 border-t border-border" data-testid="tonypro-section-2">
                        <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                            The alla prima approach
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div>
                                <h3 className="font-medium text-primary mb-3">Direct painting, no shortcuts</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Alla prima &mdash; painting wet-into-wet in a single session &mdash; demands confident brushwork and clear decision-making. Tony teaches a structured block-in process that gives you a reliable foundation for every painting session.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-medium text-primary mb-3">The Zorn palette and beyond</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Learn to work with a limited palette (black, white, ochre, red) for maximum color harmony, then expand to a full palette as your eye develops. Tony's approach builds real color intuition, not reliance on recipes.
                                </p>
                            </div>
                            <div>
                                <h3 className="font-medium text-primary mb-3">From sketch to finished painting</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed">
                                    Each session progresses through the complete painting process: gesture, block-in, value establishment, color development, and finishing. You'll see every stage of a professional portrait painted from life.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Section 3: Background */}
                    <section className="py-12 border-t border-border" data-testid="tonypro-section-3">
                        <div className="max-w-3xl">
                            <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                Training and recognition
                            </h2>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                Tony Pro earned a B.A. in Graphic Design from Cal State Northridge before developing his drawing and composition skills under the mentorship of <Link to="/glen-orbik-drawing-course" className="text-accent hover:underline">Glen Orbik</Link>, with further study under Richard Schmid. This combination &mdash; academic rigor, classical illustration tradition, and fine art mastery &mdash; gives his teaching unusual breadth.
                            </p>
                            <p className="text-muted-foreground leading-relaxed mb-4">
                                His awards include Best in Show at the 14th Annual Oil Painters of America Exhibition and First Place at the 2014 Portrait Society of America International Competition. He is a Signature Member of the California Art Club and exhibits regularly at major galleries.
                            </p>
                            <p className="text-muted-foreground leading-relaxed mb-6">
                                Tony's recent work explores the landscapes and living traditions of the Taos Pueblo, Navajo, and Hopi peoples &mdash; subjects painted with respect, authenticity, and a deeply personal artistic voice.
                            </p>
                            <div className="flex flex-wrap gap-3">
                                <Button asChild variant="outline" size="sm">
                                    <Link to="/methods/alla-prima-painting">
                                        Read: Alla Prima Painting Guide
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="sm">
                                    <Link to="/instructor/tony-pro">
                                        Full Tony Pro Biography
                                    </Link>
                                </Button>
                            </div>
                        </div>
                    </section>

                    {/* CTA */}
                    <section className="py-12 border-t border-border" data-testid="tonypro-section-cta">
                        <div className="max-w-2xl">
                            <h2 className="font-serif text-2xl md:text-3xl font-medium text-primary mb-6">
                                Start learning with Tony Pro
                            </h2>
                            <p className="text-muted-foreground leading-relaxed mb-8">
                                Choose from tiered access options: start with the first 3 classes to test the waters, or get the complete 10-lesson course for the full alla prima experience. Lifetime access, no subscriptions.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90">
                                    <Link to="/courses/7">
                                        View Course & Pricing <ArrowRight className="ml-2 h-4 w-4" />
                                    </Link>
                                </Button>
                                <Button asChild variant="outline" size="lg">
                                    <Link to="/courses">Browse All Courses</Link>
                                </Button>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default TonyProPaintingPage;
